<template>
  <li
    ref="theMenuItem"
    :class="
      bemBlockMod({
        secondary: isSecondaryLevel,
        fixed: isFixed,
        highlight: isHighlight,
        complex: hasComplexSubCategories,
        'complex-child': isComplexChild,
        block: isBlockCategory,
        'parent-block': isParentBlockCategory,
        'with-separator': withSeparator,
      })
    "
    :style="cssCustomProps"
  >
    <a
      :data-byl-categoryId="category.id"
      :class="[
        bemElement('link', {
          selected: itemSelected || isCurrentSubmenuOpen,
          highlight: isHighlight,
          secondary: isSecondaryLevel,
          block: isParentBlockCategory,
          'with-separator': withSeparator,
          'remove-uppercase': removeUppercase,
        }),
        category.classSpecial,
      ]"
      :href="href"
      @click.prevent="handleClick"
      @mouseenter="
        isPointerDesktop &&
        !isSecondaryLevel &&
        !isCurrentSubmenuOpen &&
        handleOpenSubmenu()
      "
    >
      <span :class="bemElement('label')">
        <span :class="bemElement('name')">
          {{ category.name }}
        </span>
        <span
          v-if="customTag"
          :class="bemElement('tag')"
          :style="customTagStyle"
        >
          {{ customTag }}
        </span>
      </span>
    </a>
    <TheMenuSubmenu
      v-if="subCategories"
      ref="theMenuSubmenu"
      :categories="subCategories"
      :inherited-bg-color="effectiveSubcategoryBgColor"
      :has-complex-sub-categories="hasComplexSubCategories"
      :is-open="isCurrentSubmenuOpen"
      :is-complex-submenu-child="isComplexChild"
      :is-parent-block-category="isBlockCategory"
      :complex-position="hasComplexSubCategories && itemPosition"
      @menu:close-submenu="handleCloseSubmenu"
    />
  </li>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import { getters, types } from '../store';
import bemClassesMixin from '../mixins/bemClassesMixin';
import windowResizeMixin from '../mixins/windowResizeMixin';
import TheMenuSubmenu from './TheMenuSubmenu.vue';
import getAnalyticsEventBus from '../../common/getAnalyticsEventBus';
import getFrontConfig from '../../common/getFrontConfig';
import { isAbsoluteUrl } from '../../common/utils/url';

// Id suffix of the "view all" entry the menu builds for every category whose
// render type is neither group nor block. It points at the same url as its
// category, so only this marks it apart.
const VIEW_ALL_ID_SUFFIX = '-view-all';

const analyticsEventBus = getAnalyticsEventBus();
const FRONT_CONFIG = getFrontConfig();

export default {
  name: 'TheMenuItem',
  components: {
    TheMenuSubmenu,
  },

  mixins: [bemClassesMixin, windowResizeMixin],

  props: {
    category: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    inheritedBgColor: {
      type: String,
      default: null,
    },
    isFixed: {
      type: Boolean,
      default: false,
    },
    isSecondaryLevel: {
      type: Boolean,
      default: false,
    },
    isSubmenuOpen: {
      type: Boolean,
      default: false,
    },
    isComplexChild: {
      type: Boolean,
      default: false,
    },
    isLastChild: {
      type: Boolean,
      default: false,
    },
    isParentBlockCategory: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    isCurrentSubmenuOpen: false,
    dataChangeBreakpoint: FRONT_CONFIG.responsive.bp_1025,
    isTouchDevice: false,
    itemPosition: 0,
  }),

  computed: {
    ...mapState({
      groupRenderType: (state) => state.menu.groupRenderType,
      blockRenderType: (state) => state.menu.blockRenderType,
      isClosedMenu: (state) => state.menu.isClosedMenu,
      menuCategoriesHeight: (state) => state.menu.menuCategoriesHeight,
    }),
    ...mapGetters({
      plpCategory: getters.productList.visibleCategory,
    }),
    isTouchDesktop() {
      return this.isTouchDevice && this.isBreakpointDesktop;
    },
    isPointerDesktop() {
      return !this.isTouchDevice && this.isBreakpointDesktop;
    },
    itemSelected() {
      return this.plpCategory?.parentCategories?.some(
        (category) =>
          // category id is in parentCategories array
          category.id === this.category.id ||
          // or category is view all and is plpCategory
          (`${category.id}${VIEW_ALL_ID_SUFFIX}` === this.category.id &&
            category.id === this.plpCategory?.id)
      );
    },
    hasComplexSubCategories() {
      return this.category.complexSubCategories;
    },
    isGroupCategory() {
      return this.category.byl_renderType === this.groupRenderType;
    },
    isBlockCategory() {
      return this.category.byl_renderType === this.blockRenderType;
    },
    withSeparator() {
      return !!this.category.byl_withSeparator;
    },
    removeUppercase() {
      return this.category.byl_removeUppercase;
    },
    customTag() {
      return this.category.byl_customTag;
    },
    customTagStyle() {
      return this.category.byl_customTagColor
        ? { color: this.category.byl_customTagColor }
        : null;
    },
    isHighlight() {
      return !!this.category.categoryHighlightColor;
    },
    effectiveSubcategoryBgColor() {
      return this.category.subcategoryBgColor ?? this.inheritedBgColor;
    },
    cssCustomProps() {
      return {
        '--category-bg-color': this.category.categoryBgColor ?? null,
        '--category-color': this.category.categoryColor ?? null,
        '--category-color-desktop': this.category.categoryColorDesktop ?? null,
        '--category-highlight-color':
          this.category.categoryHighlightColor ?? null,
        '--subcategory-color': this.category.subcategoryColor ?? null,
        '--subcategory-color-desktop':
          this.category.subcategoryColorDesktop ?? null,
        '--subcategory-bg-color': this.category.subcategoryBgColor ?? null,
        '--subcategory-bg-color-desktop':
          this.category.subcategoryBgColorDesktop ?? null,
        '--item-position': `${this.itemPosition}px`,
      };
    },
    subCategories() {
      let subCategories = false;
      if (this.category.subCategories) {
        subCategories = [...this.category.subCategories];
        if (!this.isGroupCategory && !this.isBlockCategory) {
          const viewAll = {
            categoryColor: this.category.categoryViewAllColor,
            categoryColorDesktop: this.category.categoryViewAllColorDesktop,
            id: `${this.category.id}${VIEW_ALL_ID_SUFFIX}`,
            name: this.$msg('menu.viewall', 'header'),
            parentCategories: this.category.parentCategories,
            subCategories: false,
            url: this.category.url,
            urlAbs: this.category.urlAbs,
          };
          subCategories.unshift(viewAll);
        }
      }
      return subCategories;
    },
    viewAllTargetId() {
      const { id } = this.category;
      return typeof id === 'string' && id.endsWith(VIEW_ALL_ID_SUFFIX)
        ? id.slice(0, -VIEW_ALL_ID_SUFFIX.length)
        : null;
    },
    hasComplexPosition() {
      // We need to set item position for category submenu when it has complex subcategories (more than 2 levels)
      // or when it is a second level category of complex parent and is the last child, because position goes like this:
      // align last child with parent until layer does not fit, then align layer to the left of the screen
      return (
        this.hasComplexSubCategories ||
        (this.isComplexChild && this.isLastChild)
      );
    },
    shouldNavigate() {
      // should navigate if category is not group type and
      return (
        !this.isGroupCategory &&
        // is not block type
        !this.isBlockCategory &&
        // does not have subcategories (no submenu to show)
        (!this.subCategories ||
          // or is desktop but not touch device (always should navigate)
          this.isPointerDesktop ||
          // or is desktop and touch device and not first level category
          (this.isTouchDesktop && this.isSecondaryLevel))
      );
    },
    href() {
      if (this.category?.byl_contentId) {
        const params = new URLSearchParams(document.location.search);
        params.append('cid', this.category.byl_contentId);
        return `${FRONT_CONFIG.urls.pageShow}?${params.toString()}`;
      }
      return this.category.url;
    },
  },

  watch: {
    isSubmenuOpen() {
      this.isCurrentSubmenuOpen = this.isSubmenuOpen;
    },
    isClosedMenu() {
      if (this.isClosedMenu) {
        this.isCurrentSubmenuOpen = false;
      } else if (
        this.category.subCategories &&
        this.itemSelected &&
        ((this.isBreakpointMobile && this.subCategories) ||
          (this.isTouchDesktop && !this.isSecondaryLevel))
      ) {
        this.handleOpenSubmenu();
      }
    },
    $windowWidth() {
      this.handleItemPosition(true);
      this.setTouchDevice();
    },
  },

  mounted() {
    this.handleItemPosition();
    this.setTouchDevice();
  },

  methods: {
    emitMenuData(event) {
      const { parentCategories } = this.category;
      const parentCategoriesNames = parentCategories.map(
        (parentCategory) => parentCategory.name
      );

      // Exception case for View all
      if (parentCategoriesNames.indexOf(this.category.name) === -1) {
        parentCategoriesNames.push(this.category.name);
      }

      const item = parentCategoriesNames.join('/').toUpperCase();

      // Send info for Analytics
      if (event === 'click') {
        analyticsEventBus.publish(
          analyticsEventBus.EVENTS.menuItemClicked,
          item
        );
      } else {
        analyticsEventBus.publish(
          analyticsEventBus.EVENTS.menuBackLinkClicked,
          item
        );
      }
    },
    handleClick() {
      this.emitMenuData('click');
      if (
        (this.isBreakpointMobile && this.subCategories) ||
        (this.isTouchDesktop && !this.isSecondaryLevel)
      ) {
        this.handleOpenSubmenu();
      }
      if (this.shouldNavigate) {
        this.$store.dispatch(types.menu.setMenuState, {});
        // Hack for Safari iOS
        // When navigation starts, Safari stops execution of CSS and animations
        // With this timeout, we wait until the menu close animation is finished
        setTimeout(() => {
          this.navigateToCategory();
        }, 350);
      }
    },
    handleCloseSubmenu() {
      this.isCurrentSubmenuOpen = false;
      this.$emit('menu:update-height');
      this.emitMenuData('back');
    },
    handleOpenSubmenu() {
      const listHeight =
        this.$refs?.theMenuSubmenu?.$refs?.list.getBoundingClientRect()
          .height || 0;
      this.handleItemPosition();
      this.isCurrentSubmenuOpen = true;
      this.$store.commit(types.menu.setMenuCategoriesHeight, listHeight);
      this.$emit('menu:category-open', this.category.id);
    },
    navigateToCategory() {
      // A view all shares its url with its category, so the view being opened
      // cannot be read back from the url: record it before navigating. Any
      // other entry clears it, so the category shows its own menu.
      this.$store.commit(
        types.productList.setViewAllCategoryId,
        this.viewAllTargetId
      );
      if (this.isParentBlockCategory) {
        this.$store.dispatch(types.categories.setCategory, this.category);
        window.location.href = this.href;
      } else if (!this.itemSelected) {
        this.$store.dispatch(types.categories.setCategory, this.category);
        if (isAbsoluteUrl(this.href)) {
          window.location.href = this.href;
        } else {
          this.$router.push(this.href);
        }
      }
    },
    calculateItemPosition() {
      return this.$refs.theMenuItem.getBoundingClientRect().left;
    },
    handleItemPosition() {
      this.itemPosition = this.calculateItemPosition();
      if (this.isBreakpointDesktop && this.hasComplexPosition) {
        this.$emit('menu:init-complex-item', this.itemPosition);
      }
    },
    setTouchDevice() {
      this.isTouchDevice = !window.matchMedia(
        '(hover: hover) and (pointer: fine)'
      ).matches;
    },
  },
};
</script>
